import {
  Stack,
  Typography,
  Box,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
} from "@mui/material";
import { PractitionerAvatar } from "./PractitionerAvatar";
import { useFhirData } from "../../../utils/useFhirData";
import { AccordionButtons } from "./AccordionButtons";
import { ScheduleExposureToggle } from "./ScheduleExposureToggle";
import {
  Circle,
  AccessTime,
  CurrencyExchange,
  SyncAlt,
  DescriptionOutlined,
  LocationOn,
  Videocam,
  Chat as ChatIcon,
  Home,
} from "@mui/icons-material";
import { useLocationContext } from "../../../contexts/LocationContext";
import { useFormatter } from "@/utils/useFormatter";
import CalendarGoogleIcon from "@/assets/CalendarGoogleIcon.png";
import { useState, useEffect } from "react";
import { GoogleCalendarSelectModal } from "../../../components/schedule/GoogleCalendarSelectModal";
import { useSessionContext } from "@/contexts/SessionContext";
import { PatchSchedule } from "@/services/fhir/schedule/PatchSchedule";
import { toast } from "react-toastify";
import { TeleconsultoriaScheduleBadge } from "src-next/modules/teleconsultoria/schedule-manager/components/TeleconsultoriaScheduleBadge";

/** Tipos de atendimento reconhecidos */
type AttendanceKind = "presencial" | "teleatendimento" | "chat" | "domicilio";

/**
 * Detecta o tipo de atendimento a partir do recurso Location.
 * Usa a extension attendance-channel (novo) com fallback para virtual-service (legado).
 */
function getAttendanceKind(locationResource: any): AttendanceKind {
  const channel = locationResource?.extension?.find((ext: any) =>
    ext.url?.includes("attendance-channel"),
  )?.valueCode;

  const isVirtual =
    locationResource?.extension?.find((ext: any) =>
      ext.url?.includes("virtual-service"),
    )?.valueBoolean === true;

  if (channel === "chat") return "chat";
  if (channel === "domicilio") return "domicilio";
  if (isVirtual) return "teleatendimento";
  return "presencial";
}


const ATTENDANCE_KIND_CONFIG: Record<
  AttendanceKind,
  { icon: React.ReactNode; colorKey: string; bgKey: string; label: string }
> = {
  presencial: {
    icon: <LocationOn sx={{ fontSize: 16 }} />,
    colorKey: "primary700.main",
    bgKey: "primary100.main",
    label: "Presencial",
  },
  teleatendimento: {
    icon: <Videocam sx={{ fontSize: 16 }} />,
    colorKey: "secondary700.main",
    bgKey: "secondary100.main",
    label: "Teleatendimento",
  },
  chat: {
    icon: <ChatIcon sx={{ fontSize: 16 }} />,
    colorKey: "error700.main",
    bgKey: "error100.main",
    label: "Chat",
  },
  domicilio: {
    icon: <Home sx={{ fontSize: 16 }} />,
    colorKey: "warning700.main",
    bgKey: "warning100.main",
    label: "Atendimento a Domicílio",
  },
};

export function AccordionHeader({
  id,
  schedule,
  setOpenAccordion,
  setRefetch,
  nextAppointments,
  nextAppointmentsPatients,
  setTabRefetch,
}: any) {
  const { location } = useLocationContext();
  const { user } = useSessionContext();
  const healthcare = useFhirData.findActor(
    schedule?.resource.actor,
    "HealthcareService",
  )?.display;

  const practitioners = useFhirData.filterActor(
    schedule?.resource.actor,
    "Practitioner/",
  );

  const locationName = useFhirData.findActor(
    schedule?.resource.actor,
    "Location",
  )?.display;

  // Ator Location pode faltar ou vir sem `reference` (agenda incompleta).
  // Sem esse id, não há como achar a Location — o header segue como presencial.
  const locationActorId = schedule?.resource?.actor
    ?.find((actor: any) => actor?.reference?.includes("Location"))
    ?.reference?.split("/")?.[1];

  const locationTeleconsulting = useFhirData.virtualService(
    location?.find((loc: any) =>
      locationActorId
        ? loc?.resource?.id?.includes(locationActorId)
        : false,
    )?.resource,
  );

  // Recurso completo da Location associada à agenda — usado para detectar o tipo de atendimento
  const scheduleLocationResource = location?.find((loc: any) =>
    locationActorId
      ? loc?.resource?.id?.includes(locationActorId)
      : false,
  )?.resource;

  // Tipo de atendimento detectado (presencial, teleatendimento, chat, domicilio)
  const attendanceKind = getAttendanceKind(scheduleLocationResource);
  const attendanceConfig = ATTENDANCE_KIND_CONFIG[attendanceKind];

  const [modalOpen, setModalOpen] = useState(false);
  const [hasBookedAppointments, setHasBookedAppointments] = useState(false);
  const [checkingAppointments, setCheckingAppointments] = useState(true);
  const [descriptionDialogOpen, setDescriptionDialogOpen] = useState(false);
  const [descriptionDraft, setDescriptionDraft] = useState<string>(
    schedule?.resource?.comment || "",
  );
  const [savingDescription, setSavingDescription] = useState(false);

  // Verifica se a agenda pertence ao profissional logado
  const isPractitionerSchedule = practitioners?.some((practitioner: any) => {
    const practitionerId = practitioner?.reference?.split("/")?.pop();
    return practitionerId?.includes(user?.username);
  });

  // Verifica se existe appointments booked usando os nextAppointments já carregados
  useEffect(() => {
    if (!nextAppointments) {
      setCheckingAppointments(true);
      return;
    }

    try {
      setCheckingAppointments(true);

      // Filtra apenas os appointments com status "booked"
      const appointmentEntries = nextAppointments?.entry?.filter(
        (el: any) =>
          el.resource?.resourceType === "Appointment" &&
          el.resource?.status === "booked",
      );

      // Verifica se há appointments futuros
      const hasBooked = appointmentEntries?.length > 0;
      setHasBookedAppointments(!!hasBooked);
    } catch (error) {
      setHasBookedAppointments(false);
    } finally {
      setCheckingAppointments(false);
    }
  }, [nextAppointments]);

  // Obtém o prazo de retorno da extensão do schedule
  const returnPeriod = schedule?.resource?.extension?.find(
    (ext: any) =>
      ext.url ===
      "https://fluxmed.com.br/fhir/StructureDefinition/schedule-return-days",
  )?.valueDuration?.value;

  const serviceCost = schedule?.resource?.extension?.find((el: any) =>
    el.url.includes("service-cost"),
  )?.valueDecimal;

  const scheduleComment =
    typeof schedule?.resource?.comment === "string"
      ? schedule.resource.comment.trim()
      : "";

  const handleOpenDescriptionDialog = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    setDescriptionDraft(schedule?.resource?.comment || "");
    setDescriptionDialogOpen(true);
  };

  const handleCloseDescriptionDialog = () => {
    if (savingDescription) return;
    setDescriptionDialogOpen(false);
  };

  const handleSaveDescription = async () => {
    const scheduleId = schedule?.resource?.id;
    if (!scheduleId) return;

    const nextValue = (descriptionDraft || "").trim();
    const currentValue = scheduleComment;

    // Nada mudou: apenas fecha.
    if (nextValue === currentValue) {
      setDescriptionDialogOpen(false);
      return;
    }

    setSavingDescription(true);
    try {
      // JSON Patch em `Schedule.comment`
      // - se não existia e agora existe => add
      // - se existia e mudou => replace
      // - se usuário removeu (vazio) => remove (com fallback para replace "")
      const operations: Array<{ op: string; path: string; value?: string }> =
        [];

      if (!currentValue && nextValue) {
        operations.push({ op: "add", path: "/comment", value: nextValue });
      } else if (currentValue && nextValue) {
        operations.push({ op: "replace", path: "/comment", value: nextValue });
      } else if (currentValue && !nextValue) {
        operations.push({ op: "remove", path: "/comment" });
      }

      try {
        await PatchSchedule(scheduleId, operations);
      } catch (err) {
        // Alguns backends rejeitam "remove" quando o campo não existe/tem restrições.
        // Fallback simples: replace com string vazia (mantém UI consistente).
        const fallbackOps =
          operations?.[0]?.op === "remove"
            ? [{ op: "replace", path: "/comment", value: "" }]
            : operations;
        await PatchSchedule(scheduleId, fallbackOps);
      }

      toast.success("Descrição da agenda atualizada.");
      setDescriptionDialogOpen(false);
      if (setRefetch) {
        setRefetch((prev: boolean) => !prev);
      }
    } catch (error) {
      toast.error("Não foi possível atualizar a descrição da agenda.");
    } finally {
      setSavingDescription(false);
    }
  };

  const handleRemoveDescription = async () => {
    setDescriptionDraft("");
  };

  return (
    <>
      <Stack
        direction="row"
        p={1}
        sx={{
          width: "100%",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        {/* Lado Esquerdo - Status e Nome */}
        <Stack direction="row" alignItems="center" spacing={2} width="30%">
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {/* Ícone do tipo de atendimento — cores via tema (Theme.tsx) */}
            <Tooltip title={attendanceConfig.label}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  // bgKey e colorKey são tokens do tema MUI (ex: "primary100.main")
                  backgroundColor: attendanceConfig.bgKey,
                  color: attendanceConfig.colorKey,
                  borderRadius: "50%",
                  width: 40,
                  height: 40,
                }}
              >
                {attendanceConfig.icon}
              </Box>
            </Tooltip>
          </Box>
          {/* Selo de teleconsultoria (só aparece nessas agendas) */}
          <TeleconsultoriaScheduleBadge schedule={schedule?.resource} />
          {/* minWidth 0: deixa o nome encolher com reticências ao lado do selo */}
          <Typography variant="body1" noWrap sx={{ minWidth: 0 }}>
            {`${schedule?.resource?.name || id} - ${healthcare}`}
          </Typography>
        </Stack>

        {/* Toggle de exposição vem primeiro para ganhar destaque visual no cabeçalho */}
        <ScheduleExposureToggle
          schedule={schedule}
          onUpdated={() => {
            if (setRefetch) {
              setRefetch((prev: boolean) => !prev);
            }
          }}
        />

        {/* Centro - Localização */}
        <Typography
          variant="body1"
          sx={{ width: "25%", textAlign: "center" }}
          noWrap
        >
          {locationName}
        </Typography>

        {/* Lado Direito - Informações e Ações */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="flex-end"
          spacing={2}
          width="30%"
        >
          {/* Informações de Custo e Retorno */}
          {(serviceCost || returnPeriod) && (
            <Stack direction="row" spacing={2} alignItems="center">
              {serviceCost && (
                <Tooltip title="Valor da consulta">
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <CurrencyExchange
                      sx={{ fontSize: "1.2rem", color: "success.main" }}
                    />
                    <Typography
                      variant="body1"
                      color="success.main"
                      fontWeight={500}
                    >
                      {useFormatter.formatCurrency(serviceCost * 100)}
                    </Typography>
                  </Box>
                </Tooltip>
              )}
              {returnPeriod && (
                <Tooltip title="Prazo de retorno">
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <SyncAlt
                      rotate={90}
                      sx={{ fontSize: "1.2rem", color: "info.main" }}
                    />
                    <Typography
                      variant="body1"
                      color="info.main"
                      fontWeight={500}
                    >
                      {returnPeriod}d
                    </Typography>
                  </Box>
                </Tooltip>
              )}
            </Stack>
          )}

          {/* Descrição da agenda (Schedule.comment) */}
          <Tooltip title={scheduleComment || "Adicionar descrição da agenda"}>
            <IconButton
              size="small"
              onClick={handleOpenDescriptionDialog}
              aria-label="Descrição da agenda"
            >
              <DescriptionOutlined sx={{ fontSize: "1.2rem" }} />
            </IconButton>
          </Tooltip>

          {/* Google Calendar */}
          {hasBookedAppointments &&
            !checkingAppointments &&
            user?.refreshTokenGoogle &&
            isPractitionerSchedule && (
              <Tooltip title="Adicionar todos atendimentos ao Google Calendar">
                <IconButton size="small" onClick={() => setModalOpen(true)}>
                  <img
                    src={CalendarGoogleIcon}
                    alt="Google Calendar"
                    style={{
                      width: 20,
                      height: 20,
                    }}
                  />
                </IconButton>
              </Tooltip>
            )}

          {/* Avatar e Botões */}
          <PractitionerAvatar filter={practitioners} />
          <AccordionButtons
            setRefetch={setRefetch}
            schedule={schedule}
            setOpenAccordion={setOpenAccordion}
          />
        </Stack>
      </Stack>

      {descriptionDialogOpen && (
        <Dialog
          open={descriptionDialogOpen}
          onClose={handleCloseDescriptionDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Descrição da agenda</DialogTitle>
          <DialogContent>
            <TextField
              value={descriptionDraft}
              onChange={(e) => setDescriptionDraft(e.target.value)}
              label="Descrição (opcional)"
              placeholder="Ex: Atende convênios X/Y. Retorno em até 30 dias. Teleconsulta apenas."
              fullWidth
              multiline
              minRows={4}
              inputProps={{ maxLength: 500 }}
              sx={{ mt: 1 }}
              disabled={savingDescription}
            />
          </DialogContent>
          <DialogActions>
            {scheduleComment && (
              <Button
                color="error"
                onClick={handleRemoveDescription}
                disabled={savingDescription}
              >
                Remover
              </Button>
            )}
            <Button
              onClick={handleCloseDescriptionDialog}
              disabled={savingDescription}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={handleSaveDescription}
              disabled={savingDescription}
            >
              Salvar
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Modal de seleção de appointments */}
      {hasBookedAppointments && (
        <GoogleCalendarSelectModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          scheduleId={schedule?.resource?.id}
          appointments={nextAppointments}
          nextAppointmentsPatients={nextAppointmentsPatients}
          onSuccess={() => {
            setModalOpen(false);
            if (setRefetch) setRefetch((prev: boolean) => !prev);
            if (setTabRefetch) setTabRefetch((prev: boolean) => !prev);
          }}
        />
      )}
    </>
  );
}
